import React, { useEffect, useState } from 'react';
import {
  Home as HomeOutlined,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/index';

import { Gradient } from '../AdminHeader/Gradient.js';

const gradient = new Gradient();

const LEGAL_ENTITY = 'C.A.H. Media LLC';
const BUSINESS_ADDRESS = '19 NW 7th Ave, Fort Lauderdale, FL 33311';
const SUPPORT_EMAIL = 'support@headshotsnow.ai';
const UPLOAD_RETENTION_DAYS = 30;
const RESULT_RETENTION_DAYS = 30;

const Terms = () => {
  const [currentLanguage, setCurrentLanguage] = useState('');

  const { t } = useTranslation();

  useEffect(() => {
    gradient.initGradient('#canvas-gradient');
    window.scrollTo(0, 0);

    return () => {};
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');

    if (view) {
      const element = document.getElementById(view);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }

    return () => {};
  }, []);

  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);

  const homeHref = currentLanguage === 'en' ? '/' : `/${currentLanguage}/`;

  return (
    <>
      <main className="bg-black">
        <div className="landing-cover" data-controller="gradient-animation">
          <div className="container">
            <div className="d-flex flex-column align-items-center">
              <h1 className="display-1 fw-semi-bold text-white text-center mb-4" style={{ zIndex: 99 }}>
                Terms, Privacy &amp; Cookies
              </h1>
              <h3 className="mb-6 text-white text-center" style={{ zIndex: 99 }}>
                Last updated and effective date: 18 June 2026
              </h3>
              <Link
                to={homeHref}
                className="btn btn-soft-light btn-sm rounded-pill tag text-nowrap"
              >
                <HomeOutlined />
                {t('button.backHome')}
              </Link>
            </div>
          </div>
          <canvas id="canvas-gradient" data-transition-in />
          <div id="canvas-overlay" />
        </div>
      </main>
      <section className="text-black bg-white">
        <div>
          <div className="container mx-auto p-6 max-w-4xl">
            <p className="mb-4">
              HeadshotsNow.ai is a digital service that generates AI professional headshots. You upload your own
              photos, choose professional styles, and receive AI-generated headshots delivered through your online
              dashboard. These pages explain the terms you agree to, how we handle your data, and how we use cookies.
            </p>

            <div className="text-start mb-6">
              <ul className="text-lg list-disc list-inside">
                <li className="mb-2 font-bold cursor-pointer" onClick={() => document.getElementById('tos').scrollIntoView({ behavior: 'smooth' })}>Terms of Service</li>
                <li className="mb-2 font-bold cursor-pointer" onClick={() => document.getElementById('privacy-policy').scrollIntoView({ behavior: 'smooth' })}>Privacy Policy</li>
                <li className="mb-2 font-bold cursor-pointer" onClick={() => document.getElementById('cookies-policy').scrollIntoView({ behavior: 'smooth' })}>Cookies Policy</li>
              </ul>
              <p className="text-sm text-gray-600 mt-2">
                Related policies:{' '}
                <Link to="/refund" className="underline">Refund Policy</Link>,{' '}
                <Link to="/delivery" className="underline">Delivery Policy</Link>,{' '}
                <Link to="/acceptable-use" className="underline">Acceptable Use Policy</Link>,{' '}
                <Link to="/contact" className="underline">Contact</Link>.
              </p>
            </div>

            {/* ============================= TERMS OF SERVICE ============================= */}
            <h2 className="mt-6 text-2xl font-bold mb-2" id="tos">Terms of Service</h2>
            <p className="mb-4">
              Welcome to HeadshotsNow.ai. These Terms of Service (&ldquo;Terms&rdquo;) are a binding agreement between you
              and {LEGAL_ENTITY} (&ldquo;HeadshotsNow.ai,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;), which operates the
              website and service at HeadshotsNow.ai. By creating an account, purchasing, or otherwise using the service,
              you agree to these Terms.
            </p>

            <h3 className="font-bold mb-2 mt-4 text-lg">1. Service Description</h3>
            <ul className="pl-4 mb-4 list-disc list-inside">
              <li>HeadshotsNow.ai is a digital, AI-powered service that generates professional-style headshots from photos you upload.</li>
              <li>Results are <span className="font-semibold">AI-generated images</span>, not photographs taken by a photographer. Output quality depends on the quality of the photos you provide.</li>
              <li>We sell one-time digital headshot packages and optional subscription credit plans. All products are digital; nothing is physically shipped.</li>
            </ul>

            <h3 className="font-bold mb-2 mt-4 text-lg">2. User Responsibilities</h3>
            <ul className="pl-4 mb-4 list-disc list-inside">
              <li>You must provide accurate account information and be at least 18 years old (or have the consent of a parent or guardian).</li>
              <li>You may only upload photos of yourself, or of a person who has given you explicit permission, and you must have all rights necessary to upload them.</li>
              <li>You agree not to upload content that is unlawful, infringing, or that violates the rights of others, and to use the service only as permitted by our <Link to="/acceptable-use" className="underline">Acceptable Use Policy</Link>.</li>
            </ul>

            <h3 className="font-bold mb-2 mt-4 text-lg">3. Payment and Billing</h3>
            <ul className="pl-4 mb-4 list-disc list-inside">
              <li>Payments are processed securely by Stripe. We do not store your full card details.</li>
              <li>Full payment is required before your headshots are generated. Prices are shown at checkout in U.S. dollars unless stated otherwise.</li>
              <li><span className="font-semibold">One-time packages</span> are charged once. <span className="font-semibold">Subscription plans</span> (where chosen) renew automatically each billing period until you cancel; you can cancel anytime from your dashboard or the billing portal, effective at the end of the current period.</li>
            </ul>

            <h3 className="font-bold mb-2 mt-4 text-lg">4. Digital Product Delivery</h3>
            <p className="mb-4">
              This is a digital product. There is no physical shipping. Generated headshots are delivered to your online
              dashboard (and, where applicable, by email). Typical turnaround is shown on the pricing section and in our{' '}
              <Link to="/delivery" className="underline">Delivery Policy</Link>. Because results are AI-generated, exact
              delivery times and visual results may vary.
            </p>

            <h3 className="font-bold mb-2 mt-4 text-lg">5. AI Results Disclaimer</h3>
            <p className="mb-4">
              All images produced by the service are generated by artificial intelligence. They are not real photographs
              and may contain imperfections or inaccuracies. Results depend heavily on the clarity, lighting, and variety
              of the photos you upload. We do not guarantee any specific result, likeness accuracy, or suitability for a
              particular purpose.
            </p>

            <h3 className="font-bold mb-2 mt-4 text-lg">6. Ownership and License of Your Headshots</h3>
            <ul className="pl-4 mb-4 list-disc list-inside">
              <li>You own the final AI-generated headshots you receive and may use them for personal and commercial professional purposes (LinkedIn, resumes, company bios, business cards, speaker bios, email signatures, and similar).</li>
              <li>You retain all rights to the photos you upload. You grant us a limited license to process them solely to create your headshots and operate the service.</li>
              <li>We do <span className="font-semibold">not</span> use your photos or headshots to advertise you, and we do not display your images publicly without your permission.</li>
            </ul>

            <h3 className="font-bold mb-2 mt-4 text-lg">7. Prohibited Use</h3>
            <p className="mb-4">
              You may not use the service to create identity or government documents, to impersonate or defraud anyone, to
              produce adult or sexual content, to harass, for political deception, for any illegal activity, or to create
              misleading deepfakes. See the full <Link to="/acceptable-use" className="underline">Acceptable Use Policy</Link>.
              We may suspend or terminate accounts that violate these rules.
            </p>

            <h3 className="font-bold mb-2 mt-4 text-lg">8. Refunds</h3>
            <p className="mb-4">
              Our refund commitment is described in our <Link to="/refund" className="underline">Refund Policy</Link>. In
              short: if you do not receive at least one usable professional headshot, contact {SUPPORT_EMAIL} within 14
              days of purchase, and we will issue a refund where no final images have been downloaded and the request
              meets our refund conditions.
            </p>

            <h3 className="font-bold mb-2 mt-4 text-lg">9. Limitation of Liability</h3>
            <p className="mb-4">
              To the maximum extent permitted by law, the service is provided &ldquo;as is.&rdquo; HeadshotsNow.ai is not
              liable for indirect, incidental, or consequential damages, and our total liability is limited to the amount
              you paid for the order giving rise to the claim. This does not affect statutory rights you may have as a
              consumer.
            </p>

            <h3 className="font-bold mb-2 mt-4 text-lg">10. Termination</h3>
            <p className="mb-4">
              You may stop using the service and request deletion of your data at any time. We may suspend or terminate
              access for violations of these Terms or the Acceptable Use Policy.
            </p>

            <h3 className="font-bold mb-2 mt-4 text-lg">11. Contact</h3>
            <p className="mb-4">
              {LEGAL_ENTITY}{BUSINESS_ADDRESS ? `, ${BUSINESS_ADDRESS}` : ''}. Questions about these Terms: {SUPPORT_EMAIL}.
            </p>

            {/* ============================= PRIVACY POLICY ============================= */}
            <h2 className="mt-10 text-2xl font-bold mb-2" id="privacy-policy">Privacy Policy</h2>
            <p className="mb-4">
              {LEGAL_ENTITY} (&ldquo;HeadshotsNow.ai&rdquo;) is committed to protecting your privacy. This policy explains
              what we collect, how we use it, who processes it on our behalf, and how long we keep it.
            </p>

            <h3 className="font-bold mb-2 mt-4 text-lg">1. Data We Collect</h3>
            <ul className="pl-4 mb-4 list-disc list-inside">
              <li><span className="font-semibold">Account data:</span> name and email address.</li>
              <li><span className="font-semibold">Payment data:</span> processed by Stripe. We receive confirmation and limited billing details but not your full card number.</li>
              <li><span className="font-semibold">Photos you upload:</span> the images you provide so the AI can generate your headshots.</li>
              <li><span className="font-semibold">Usage and analytics data:</span> basic, mostly aggregated data used to operate and improve the service.</li>
            </ul>

            <h3 className="font-bold mb-2 mt-4 text-lg">2. How We Use Your Data</h3>
            <ul className="pl-4 mb-4 list-disc list-inside">
              <li>To generate and deliver your AI headshots.</li>
              <li>To process payments, provide support, and send important account or product updates (you can opt out of marketing emails).</li>
              <li>To maintain the security and integrity of the service.</li>
            </ul>

            <h3 className="font-bold mb-2 mt-4 text-lg">3. How Uploaded Photos Are Handled</h3>
            <p className="mb-4">
              Your uploaded photos are used <span className="font-semibold">solely</span> to generate your own headshots.
              We do not sell your photos, we do not share them for advertising, and we do not make them public.
            </p>

            <h3 className="font-bold mb-2 mt-4 text-lg">4. How AI Processing Works &amp; Model Training</h3>
            <p className="mb-4">
              To create your headshots, we train a <span className="font-semibold">private, single-purpose AI model</span> on
              the photos you upload, and we use that model only to generate headshots for you. We do{' '}
              <span className="font-semibold">not</span> use your photos to train shared, general-purpose, or foundation
              models, and we do not use them to train models for other customers.
            </p>

            <h3 className="font-bold mb-2 mt-4 text-lg">5. Subprocessors</h3>
            <p className="mb-2">We rely on a small number of trusted providers to operate the service:</p>
            <ul className="pl-4 mb-4 list-disc list-inside">
              <li><span className="font-semibold">Replicate</span> — runs the AI model training and image generation.</li>
              <li><span className="font-semibold">Google Firebase / Google Cloud</span> — authentication, database, file storage, and hosting.</li>
              <li><span className="font-semibold">OpenAI</span> — optional text/prompt assistance (no photos are sent for this).</li>
              <li><span className="font-semibold">Stripe</span> — payment processing.</li>
            </ul>

            <h3 className="font-bold mb-2 mt-4 text-lg">6. Data Retention &amp; Deletion</h3>
            <ul className="pl-4 mb-4 list-disc list-inside">
              <li>Uploaded photos are automatically deleted within {UPLOAD_RETENTION_DAYS} days of upload.</li>
              <li>Generated headshots are automatically deleted within {RESULT_RETENTION_DAYS} days of creation. Please download the images you want to keep before then.</li>
              <li>You can request deletion of your data at any time by emailing {SUPPORT_EMAIL}, and we will delete it promptly. Basic account and transaction records may be retained where required for legal or accounting purposes.</li>
            </ul>

            <h3 className="font-bold mb-2 mt-4 text-lg">7. Your Rights</h3>
            <p className="mb-4">
              You may request access to, correction of, or deletion of your personal data by contacting {SUPPORT_EMAIL}.
              We do not sell your personal information to third parties.
            </p>

            <h3 className="font-bold mb-2 mt-4 text-lg">8. Contact</h3>
            <p className="mb-4">
              {LEGAL_ENTITY}{BUSINESS_ADDRESS ? `, ${BUSINESS_ADDRESS}` : ''}. Privacy questions: {SUPPORT_EMAIL}.
            </p>

            {/* ============================= COOKIES POLICY ============================= */}
            <h2 className="mt-10 text-2xl font-bold mb-2" id="cookies-policy">Cookies Policy</h2>
            <p className="mb-4">
              At HeadshotsNow.ai we use cookies and similar technologies to operate the website, remember your
              preferences, analyze performance, and (where applicable) measure marketing. This policy explains what
              cookies are and your choices.
            </p>
            <h3 className="font-bold mb-2 mt-4 text-lg">1. What Are Cookies?</h3>
            <p className="mb-4">
              Cookies are small text files stored on your device when you visit a website. They help the site remember
              your actions and preferences over time.
            </p>
            <h3 className="font-bold mb-2 mt-4 text-lg">2. How We Use Cookies</h3>
            <ul className="pl-4 mb-4 list-disc list-inside">
              <li><span className="font-semibold">Strictly necessary</span> — sign-in sessions, security, and core functionality.</li>
              <li><span className="font-semibold">Performance/analytics</span> — understand how the site is used so we can improve it (e.g. Google Analytics).</li>
              <li><span className="font-semibold">Functionality</span> — remember preferences such as language.</li>
              <li><span className="font-semibold">Advertising</span> — where used, to measure and improve campaigns.</li>
            </ul>
            <h3 className="font-bold mb-2 mt-4 text-lg">3. Managing Cookies</h3>
            <p className="mb-4">
              Most browsers let you view, delete, and block cookies through their settings. Blocking some cookies may
              affect site functionality. You can also use industry opt-out tools for advertising cookies.
            </p>
            <h3 className="font-bold mb-2 mt-4 text-lg">4. Contact</h3>
            <p className="mb-4">
              Questions about cookies: {SUPPORT_EMAIL}.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Terms;
